/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  formatFecha,
  Titulo,
  CampoInformacion,
  urlBaseAPI,
} from "../../shared";
import { Card } from "primereact/card";
import { Image } from "primereact/image";

const API_URL = urlBaseAPI();

export default function MuestraPublica() {
  const { hash } = useParams();
  const [muestra, setMuestra] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hash) return;
    accessAPI(
      "GET",
      `muestra/publica/${hash}`,
      null,
      (resp) => setMuestra(resp),
      (resp) => setError(resp?.[0]?.msg || "No se pudo cargar la muestra"),
    );
  }, [hash]);

  // Fondo violeta acorde al resto de la app pública.
  const fondoBlanco = {
    background: "var(--principal)",
    minHeight: "calc(100vh - var(--alturaFooter))",
  };

  if (error) {
    return (
      <div css={fondoBlanco}>
        <div css={{ padding: "1.5em", maxWidth: "900px", margin: "0 auto" }}>
          <Card>
            <Titulo>Muestra no encontrada</Titulo>
            <p>{error}</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!muestra) {
    return (
      <div css={fondoBlanco}>
        <div css={{ padding: "1.5em", maxWidth: "900px", margin: "0 auto" }}>
          <Card>
            <p>Cargando…</p>
          </Card>
        </div>
      </div>
    );
  }

  if (muestra.estado === "PENDIENTE") {
    return (
      <div css={fondoBlanco}>
        <div css={{ padding: "1.5em", maxWidth: "900px", margin: "0 auto" }}>
          <Titulo etiquetas={[{ texto: "Pendiente de ingreso", neutro: true }]}>
            Muestra {muestra.numero}
          </Titulo>
          <Card>
            <p>Esta muestra todavía no fue ingresada.</p>
          </Card>
        </div>
      </div>
    );
  }

  const etiquetaEstado = muestra.desactivada
    ? { texto: "Desactivada", error: true }
    : muestra.retirada
      ? { texto: "Retirada de la caja", alerta: true }
      : { texto: "Ingresada", ok: true };

  const imagenes = muestra.imagenes || [];

  return (
    <div css={fondoBlanco}>
      <div css={{ padding: "1.5em", maxWidth: "900px", margin: "0 auto" }}>
        <Titulo etiquetas={[etiquetaEstado]}>Muestra {muestra.numero}</Titulo>

        <Card>
          <Titulo>Información general</Titulo>
          <div className="grid">
            {muestra.producto?.proceso?.cliente?.razonsocial && (
              <CampoInformacion nombreCampo="Cliente">
                {muestra.producto.proceso.cliente.razonsocial}
              </CampoInformacion>
            )}
            {muestra.producto?.proceso && (
              <CampoInformacion nombreCampo="Proceso">
                {`${muestra.producto.proceso.nombre || ""}${
                  muestra.producto.proceso.nombrefantasia
                    ? ` - ${muestra.producto.proceso.nombrefantasia}`
                    : ""
                }`}
              </CampoInformacion>
            )}
            {muestra.producto && (
              <CampoInformacion nombreCampo="Producto">
                {muestra.producto.id}
                {muestra.producto.notas ? ` - ${muestra.producto.notas}` : ""}
              </CampoInformacion>
            )}
            {muestra.actividad?.actividadtipo?.nombre && (
              <CampoInformacion nombreCampo="Actividad">
                {muestra.actividad.actividadtipo.nombre}
                {muestra.actividad.fechacreacion
                  ? ` - ${formatFecha(muestra.actividad.fechacreacion)}`
                  : ""}
              </CampoInformacion>
            )}
            {muestra.caja && (
              <CampoInformacion nombreCampo="Caja">
                {muestra.caja.numero}
                {muestra.caja.descripcion ? ` - ${muestra.caja.descripcion}` : ""}
              </CampoInformacion>
            )}
            {muestra.fechaadquisicion && (
              <CampoInformacion nombreCampo="Adquirida">
                {formatFecha(muestra.fechaadquisicion)}
              </CampoInformacion>
            )}
            {muestra.fechaingreso && (
              <CampoInformacion nombreCampo="Ingresada">
                {formatFecha(muestra.fechaingreso)}
              </CampoInformacion>
            )}
            {muestra.retirada && muestra.fecharetiro && (
              <CampoInformacion nombreCampo="Retirada">
                {formatFecha(muestra.fecharetiro)}
              </CampoInformacion>
            )}
            {muestra.desactivada && muestra.fechadesactivacion && (
              <CampoInformacion nombreCampo="Desactivada">
                {formatFecha(muestra.fechadesactivacion)}
              </CampoInformacion>
            )}
            {muestra.comprobanteArchivo && (
              <CampoInformacion nombreCampo="Comprobante">
                <a
                  href={`${API_URL}/archivo/${muestra.comprobanteArchivo.hash}/preview`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {muestra.comprobanteArchivo.nombredescarga}
                </a>
              </CampoInformacion>
            )}
            {muestra.notas && (
              <CampoInformacion nombreCampo="Notas" anchoCompleto>
                {muestra.notas}
              </CampoInformacion>
            )}
          </div>
        </Card>

        {imagenes.length > 0 && (
          <Card css={{ marginTop: "1.5rem" }}>
            <Titulo>Imágenes</Titulo>
            <div
              css={{
                marginTop: "1rem",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: "0.75rem",
              }}
            >
              {imagenes.map((img) => (
                <div
                  key={img.archivo.hash}
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Image
                    src={`${API_URL}/archivo/${img.archivo.hash}/preview`}
                    alt={img.archivo.nombredescarga}
                    preview
                    imageStyle={{
                      width: "160px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
